import { z } from 'zod';
import { allocationOperationSchema, APARTMENT_ROW_STATUSES } from './apartment-sheet.js';
import { MAX_CELL_LENGTH } from './unit-cells.js';
// The Parking and Storage sheets (docs/16 §3, MD-2A): one row per physical space, identified by
// its location (structure), level and number, with the apartment it is allocated to. Spaces can be
// created here in bulk; assignments, ends and transfers follow the same rules as the Apartments
// sheet (explicit, confirmed, previewed, atomic).
export const SPACE_KINDS = ['parking', 'storage'];
export const SPACE_FIELDS = ['location', 'level', 'number', 'building', 'apartment'];
export const SPACE_COLUMNS = {
    parking: [
        { key: 'location', header: 'חניון', secondary: null, group: 'space' },
        { key: 'level', header: 'מרתף', secondary: null, group: 'space' },
        { key: 'number', header: 'מספר חניה', secondary: null, group: 'space' },
        { key: 'building', header: 'בנין', secondary: 'דירה מוצמדת', group: 'apartment' },
        { key: 'apartment', header: 'דירה', secondary: 'דירה מוצמדת', group: 'apartment' },
    ],
    storage: [
        { key: 'location', header: 'מבנה מחסנים', secondary: null, group: 'space' },
        { key: 'level', header: 'מרתף', secondary: null, group: 'space' },
        { key: 'number', header: 'מספר מחסן', secondary: null, group: 'space' },
        { key: 'building', header: 'בנין', secondary: 'דירה מוצמדת', group: 'apartment' },
        { key: 'apartment', header: 'דירה', secondary: 'דירה מוצמדת', group: 'apartment' },
    ],
};
export const spaceSheetRowSchema = z
    .object({
    unitId: z.uuid(),
    rowVersion: z.number().int().min(1),
    location: z.string(),
    /** The level label ('' when the space has no level). */
    level: z.string(),
    number: z.string(),
    /** The allocation in effect now, if any. */
    allocation: z
        .object({
        allocationId: z.uuid(),
        apartmentId: z.uuid(),
        building: z.string(),
        apartment: z.string(),
    })
        .strict()
        .nullable(),
    /** A future allocation or a scheduled end exists: assignment changes wait (MD-2A). */
    scheduled: z.boolean(),
})
    .strict();
export const spaceSheetResponseSchema = z
    .object({
    items: z.array(spaceSheetRowSchema),
    page: z
        .object({ limit: z.number().int(), offset: z.number().int(), total: z.number().int() })
        .strict(),
})
    .strict();
export const SPACE_BATCH_MAX_ROWS = 500;
export const SPACE_CLEARABLE = ['level', 'apartment'];
const cells = z
    .object(Object.fromEntries(SPACE_FIELDS.map((field) => [field, z.string().max(MAX_CELL_LENGTH).optional()])))
    .strict();
const clientRowId = z.string().regex(/^[A-Za-z0-9_-]{1,64}$/);
export const spaceBatchRowSchema = z.discriminatedUnion('op', [
    z.object({ op: z.literal('create'), clientRowId, cells }).strict(),
    z
        .object({
        op: z.literal('update'),
        clientRowId,
        unitId: z.uuid(),
        rowVersion: z.number().int().min(1),
        cells,
        clear: z
            .array(z.enum(SPACE_CLEARABLE))
            .max(SPACE_CLEARABLE.length)
            .refine((list) => new Set(list).size === list.length, 'duplicate')
            .default([]),
    })
        .strict(),
]);
export const spaceBatchRequestSchema = z
    .object({
    batchId: z.uuid(),
    rows: z.array(spaceBatchRowSchema).min(1).max(SPACE_BATCH_MAX_ROWS),
    confirmedEnds: z.array(z.uuid()).max(SPACE_BATCH_MAX_ROWS).default([]),
})
    .strict()
    .superRefine((batch, ctx) => {
    const seenRows = new Set();
    const seenUnits = new Set();
    batch.rows.forEach((row, index) => {
        if (seenRows.has(row.clientRowId))
            ctx.addIssue({
                code: 'custom',
                path: ['rows', index, 'clientRowId'],
                message: 'duplicate_row',
            });
        seenRows.add(row.clientRowId);
        if (row.op === 'update') {
            if (seenUnits.has(row.unitId))
                ctx.addIssue({
                    code: 'custom',
                    path: ['rows', index, 'unitId'],
                    message: 'duplicate_row',
                });
            seenUnits.add(row.unitId);
        }
    });
});
/** The same answer shape as the Apartments sheet: rows, statuses and allocation operations. */
export const spaceBatchResponseSchema = z
    .object({
    batchId: z.uuid(),
    committed: z.boolean(),
    replayed: z.boolean(),
    summary: z
        .object({
        ...Object.fromEntries(APARTMENT_ROW_STATUSES.map((s) => [s, z.number().int().min(0)])),
        assign: z.number().int().min(0),
        end: z.number().int().min(0),
        retain: z.number().int().min(0),
        spaces: z.number().int().min(0),
    })
        .strict(),
    rows: z.array(z
        .object({
        clientRowId,
        status: z.enum(APARTMENT_ROW_STATUSES),
        unitId: z.uuid().nullable(),
        rowVersion: z.number().int().min(1).nullable(),
        fields: z.record(z.string(), z.string()),
        allocations: z.array(allocationOperationSchema),
    })
        .strict()),
})
    .strict();
//# sourceMappingURL=space-sheet.js.map